/**
 * placeToComponents.js
 *
 * Translates a Google Maps `Place` (the modern Places API object returned by
 * `placePrediction.toPlace()` after `fetchFields`) into the flat set of address
 * fields our forms and API expect.
 *
 * This is the contract between the Places API and the organization/site records
 * we create, so it is kept as a pure function and covered by unit tests.
 */

/**
 * Extracts a single address component from a Place by matching its type.
 *
 * @param {Array} addressComponents - The addressComponents array from a Place.
 * @param {string} type - The component type to look for, such as 'locality'.
 * @returns {string} The short form of the matching component, or an empty string.
 */
function getShortComponent(addressComponents, type) {
  const component = addressComponents.find((c) => (c.types || []).includes(type));
  return component ? (component.shortText || '') : '';
}

/**
 * Maps a Place to the address fields used by the organization and site forms.
 *
 * @param {object} place - A Place with `addressComponents` already fetched.
 * @returns {object} The flat address fields, with empty strings for anything absent.
 */
export default function placeToComponents(place) {
  const addressComponents = place?.addressComponents || [];

  const streetNumber = getShortComponent(addressComponents, 'street_number');
  const route = getShortComponent(addressComponents, 'route');

  return {
    place_id: place?.id,
    name: place?.displayName,
    /*
     * Note: this reproduces the legacy behaviour exactly, including the leading
     * space produced when a route is present but the street number is not.
     */
    addressLine1: `${streetNumber}${route && ` ${route}` || ''}`,
    addressLine2: '',
    addressLine3: '',
    city: getShortComponent(addressComponents, 'locality'),
    state: getShortComponent(addressComponents, 'administrative_area_level_1'),
    zipCode: getShortComponent(addressComponents, 'postal_code')
  };
}
